import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles, Mic } from "lucide-react";
import axios from "axios";
import MasonryGrid from "../components/StoryCard";

const API = "/api";
const PAGE_SIZE = 12;

function WhyOfTheDay() {
  const [story, setStory] = useState(null);
  useEffect(() => {
    axios.get(`${API}/stories/why-of-the-day`).then(res => setStory(res.data)).catch(() => {});
  }, []);
  if (!story) return null;
  const quote = story.pull_quote || (story.why.length > 220 ? story.why.slice(0, 220).trim() + "…" : story.why);
  return (
    <Link
      to={`/story/${story.id}`}
      data-testid="why-of-the-day"
      className="brutal-card group block p-6 md:p-8 mb-10"
      style={{ background: '#fff' }}
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="sticker -rotate-1" style={{ background: '#E91E8C', color: '#fff' }}>why of the day</span>
        <span className="hand-note text-lg" style={{ color: '#888780' }}>a new one every day</span>
      </div>
      <p className="font-sans text-lg md:text-2xl leading-snug" style={{ color: '#16130F', fontWeight: 600, maxWidth: '860px' }}>
        <span className="highlight-mark">&ldquo;{quote}&rdquo;</span>
      </p>
      <div className="flex items-center justify-between mt-5">
        <p className="text-sm font-sans font-bold" style={{ color: '#16130F' }}>
          — {story.display_name || story.name}
          <span className="font-medium" style={{ color: '#8A8378' }}>{story.role ? ` · ${story.role}` : ''}</span>
        </p>
        <span className="text-xs font-sans font-bold underline underline-offset-4 transition-colors group-hover:text-[#E91E8C]" style={{ color: '#16130F' }}>
          read the why &rarr;
        </span>
      </div>
    </Link>
  );
}

function faceAwareThumbnail(url, width = 400, height = 400) {
  if (!url) return url;
  if (url.includes("res.cloudinary.com") && url.includes("/upload/")) {
    return url.replace("/upload/", `/upload/c_fill,g_face,w_${width},h_${height},q_auto/`);
  }
  return url;
}

/* Hero photo collage — faces of people who answered */
function HeroCollage({ stories }) {
  // Get first 6 stories with images
  const withPhotos = stories.filter(s => s.media_url && s.media_type === "image").slice(0, 6);
  const cells = [];

  for (let i = 0; i < 6; i++) {
    if (i < withPhotos.length) {
      cells.push({ type: "photo", url: faceAwareThumbnail(withPhotos[i].media_url) });
    } else {
      cells.push({ type: "placeholder" });
    }
  }

  return (
    <div className="grid grid-cols-3 grid-rows-2 gap-2.5 w-full opacity-0 animate-fade-in delay-200" style={{ animationFillMode: "forwards" }}>
      {cells.map((cell, i) => (
        <div
          key={i}
          className="relative overflow-hidden"
          style={{ borderRadius: '10px', aspectRatio: '1' }}
        >
          {cell.type === "photo" ? (
            <>
              <img
                src={cell.url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Amber overlay */}
              <div className="absolute inset-0" style={{ background: 'var(--iab-gradient-photo)' }} />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: '#FAEEDA' }}>
              <span style={{ fontFamily: 'var(--genz-font-display)', fontSize: '72px', color: '#16130F', opacity: 0.15, lineHeight: 1 }}>
                &ldquo;
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function HomePage() {
  const [allStories, setAllStories] = useState([]);
  const [stories, setStories] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [autoCap, setAutoCap] = useState(PAGE_SIZE * 4);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const isFiltering = search || cityFilter || roleFilter || themeFilter;

  // Fetch all stories + filter options
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/stories?limit=500`),
      axios.get(`${API}/stories/filters`),
    ]).then(([storiesRes, filtersRes]) => {
      setAllStories(storiesRes.data.stories);
      setStories(storiesRes.data.stories);
      setFilters(filtersRes.data);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, []);

  // Apply filters client-side
  useEffect(() => {
    if (!allStories.length) return;
    let filtered = [...allStories];

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(s =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.display_name || "").toLowerCase().includes(q) ||
        (s.role || "").toLowerCase().includes(q) ||
        (s.city || "").toLowerCase().includes(q) ||
        (s.why || "").toLowerCase().includes(q)
      );
    }
    if (cityFilter) {
      filtered = filtered.filter(s => s.city === cityFilter);
    }
    if (roleFilter) {
      filtered = filtered.filter(s => s.role === roleFilter);
    }
    if (themeFilter) {
      filtered = filtered.filter(s => (s.themes || []).includes(themeFilter));
    }

    setStories(filtered);
    setDisplayCount(PAGE_SIZE);
  }, [search, cityFilter, roleFilter, themeFilter, allStories]);

  // Silent infinite scroll — auto-loads up to autoCap, then pauses so the page bottom stays reachable
  useEffect(() => {
    if (loading || displayCount >= stories.length || displayCount >= autoCap) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayCount < stories.length && displayCount < autoCap) {
          setDisplayCount(prev => Math.min(prev + PAGE_SIZE, stories.length, autoCap));
        }
      },
      { rootMargin: "400px" }
    );

    observerRef.current = observer;
    if (sentinelRef.current) observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [loading, displayCount, stories.length, autoCap]);

  const clearFilters = () => {
    setSearch("");
    setCityFilter("");
    setRoleFilter("");
    setThemeFilter("");
  };

  const visibleStories = stories.slice(0, displayCount);
  const totalApproved = filters?.total || allStories.length;

  return (
    <div data-testid="homepage" className="min-h-screen">
      {/* Hero */}
      <section data-testid="hero-section" className="relative overflow-hidden" style={{ background: '#FBF7F0' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 lg:py-24">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Left — the question */}
            <div className="flex-1 max-w-xl">
              <div className="flex flex-wrap gap-3 mb-8 opacity-0 animate-fade-in">
                <span className="sticker -rotate-1" style={{ background: '#fff', color: '#16130F' }}>no ads</span>
                <span className="sticker rotate-1" style={{ background: '#fff', color: '#16130F' }}>no algorithms</span>
                <span className="sticker -rotate-2" style={{ background: '#E91E8C', color: '#fff' }}>just why.</span>
              </div>
              <h1
                className="opacity-0 animate-fade-in delay-100"
                style={{
                  fontFamily: 'var(--genz-font-display)',
                  fontSize: 'clamp(2.6rem, 4.6vw, 4.4rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.03em',
                  color: '#0F0F0F',
                  fontWeight: 800,
                }}
              >
                <span className="marker-underline">why</span> do you do
                <br />
                what you do?
              </h1>
              <p className="mt-8 text-lg md:text-xl font-sans text-[#4B5563] leading-relaxed max-w-xl opacity-0 animate-fade-in delay-200">
                real people. real reasons. add yours.
              </p>
              <p className="mt-3 hand-note opacity-0 animate-fade-in delay-200" style={{ fontSize: '24px', color: '#E91E8C' }}>
                found your why or still looking — both count here &#10022;
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4 opacity-0 animate-fade-in delay-300">
                <button
                  data-testid="hero-talk-to-mira"
                  onClick={() => window.dispatchEvent(new CustomEvent('openMiraSeeking'))}
                  className="brutal-btn inline-flex items-center gap-2.5 px-8 py-4 text-base"
                  style={{ background: '#E91E8C', color: '#fff' }}
                >
                  talk it out with mira
                  <ArrowRight size={18} />
                </button>
                <Link
                  to="/submit"
                  data-testid="hero-form-link"
                  className="brutal-btn inline-flex items-center gap-2 px-6 py-3.5 text-sm"
                  style={{ background: '#fff', color: '#16130F' }}
                >
                  share your why
                </Link>
                <a
                  href="#stories"
                  data-testid="hero-read-stories"
                  className="text-sm font-sans font-bold underline underline-offset-4 transition-colors hover:text-[#E91E8C]"
                  style={{ color: '#0F0F0F' }}
                >
                  read the whys &darr;
                </a>
              </div>
              <p className="mt-5 opacity-0 animate-fade-in delay-300 hand-note" style={{ fontSize: '17px', color: '#888780' }}>
                mira listens. mira remembers. mira never judges.
              </p>
            </div>

            {/* Right — the people who answered */}
            <div className="flex-1 w-full max-w-md lg:max-w-lg hidden md:block">
              {!loading && stories.length > 0 && (
                <HeroCollage stories={stories} />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Marquee divider */}
      <div className="genz-marquee" aria-hidden="true">
        <div className="genz-marquee-track">
          {Array(2).fill("real people \u2726 real reasons \u2726 no filters \u2726 no feeds \u2726 just why \u2726 found it or still looking \u2726 both count \u2726 ").map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </div>
      </div>

      {/* Meet Mira Section */}
      <section data-testid="meet-mira-section" style={{ background: '#FBF7F0', borderTop: '0.5px solid #E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">
            {/* Left — text */}
            <div className="flex-1 max-w-lg">
              <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold mb-5" style={{ color: '#E91E8C' }}>
                meet mira
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl leading-snug mb-6" style={{ fontFamily: 'var(--genz-font-display)', fontWeight: 700, color: '#0F0F0F', letterSpacing: '-0.02em' }}>
                she asks. she listens.
                <br />
                <span style={{ color: '#E91E8C' }}>she finds what you didn't know you were saying.</span>
              </h2>
              <p className="mb-8 font-sans" style={{ fontSize: '17px', lineHeight: '1.8', color: '#4B5563' }}>
                mira is not a form. she's a conversation. she'll ask what you do, why you do it, and one question nobody has ever thought to ask you before. your story gets saved exactly how you told it — your words, your voice, your truth.
              </p>
              <div className="space-y-3 mb-10">
                {[
                  "talks to you in real time",
                  "speaks and listens in your voice",
                  "polishes your words gently before saving",
                ].map((line) => (
                  <div key={line} className="flex items-center gap-3">
                    <span style={{ color: '#E91E8C', fontSize: '12px' }}>&#10022;</span>
                    <span className="text-sm font-sans font-medium" style={{ color: '#4B5563' }}>{line}</span>
                  </div>
                ))}
              </div>
              <button
                data-testid="meet-mira-open"
                onClick={() => {
                  const orb = document.querySelector('[data-testid="mira-orb"]');
                  if (orb) orb.click();
                }}
                className="brutal-btn inline-flex items-center gap-2.5 px-7 py-3.5 text-sm"
                style={{ background: '#E91E8C', color: '#fff' }}
              >
                start talking
                <ArrowRight size={16} />
              </button>
            </div>

            {/* Right — live Mira preview */}
            <div className="flex-1 w-full max-w-sm lg:max-w-md">
              <div
                data-testid="mira-preview-inline"
                className="rounded-2xl overflow-hidden shadow-xl"
                style={{ background: '#1a1a2e' }}
              >
                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-4" style={{ background: 'linear-gradient(135deg, #E91E8C 0%, #c2185b 100%)' }}>
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <Sparkles size={16} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold text-sm">Mira</p>
                    <p className="text-xs text-white/60 font-sans">Listening...</p>
                  </div>
                </div>
                {/* Messages */}
                <div className="px-5 py-5 space-y-3">
                  <div className="max-w-[85%] rounded-2xl rounded-bl-sm px-4 py-3 text-sm leading-relaxed" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)' }}>
                    Hello. I have one question for you. But first — what do you do? Not your title. Your real work. What do you actually do every day?
                  </div>
                  {/* Chips */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {[
                      { label: "Share my why", pink: true },
                      { label: "I don't know my why yet", pink: true },
                      { label: "Surprise me, Mira", pink: true },
                    ].map((chip) => (
                      <button
                        key={chip.label}
                        onClick={() => {
                          const orb = document.querySelector('[data-testid="mira-orb"]');
                          if (orb) orb.click();
                        }}
                        className="px-3.5 py-2 rounded-full text-xs font-sans font-medium border transition-all duration-200 hover:scale-105 cursor-pointer"
                        style={{ borderColor: 'rgba(233,30,140,0.3)', color: 'rgba(233,30,140,0.9)', background: 'rgba(233,30,140,0.08)' }}
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Input bar */}
                <div className="px-4 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                  <div
                    className="flex items-center gap-2 rounded-xl px-3 py-2.5 cursor-pointer"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                    onClick={() => {
                      const orb = document.querySelector('[data-testid="mira-orb"]');
                      if (orb) orb.click();
                    }}
                  >
                    <span className="flex-1 text-sm font-sans" style={{ color: 'rgba(255,255,255,0.25)' }}>Say something to Mira...</span>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'transparent' }}>
                      <Mic size={13} style={{ color: 'rgba(255,255,255,0.4)' }} />
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center mt-4 hand-note text-base" style={{ color: '#4B5563' }}>
                You don't have to fill in a form. You can just talk to her.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stories */}
      <section id="stories" data-testid="stories-grid-section" className="py-16 md:py-24" style={{ background: '#FBF7F0' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {!isFiltering && <WhyOfTheDay />}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl tracking-tight" style={{ fontFamily: 'var(--genz-font-display)', fontWeight: 800, color: '#0F0F0F' }}>
              the whys{' '}
              <span className="sticker rotate-2 ml-2 align-middle" style={{ background: '#fff', color: '#16130F', fontSize: '12px' }}>and counting</span>
            </h2>
            {isFiltering && (
              <button
                data-testid="clear-filters"
                onClick={clearFilters}
                className="text-xs font-sans font-bold underline transition-colors hover:opacity-70"
                style={{ color: '#E91E8C' }}
              >
                clear filters
              </button>
            )}
          </div>

          {/* Filter bar — desktop */}
          {!loading && allStories.length > 0 && (
            <>
              <div data-testid="filter-bar" className="hidden md:block iab-glass-bar rounded-2xl px-6 py-5 mb-8 sticky top-16 z-20">
                <div className="flex items-end gap-5">
                  {/* Search */}
                  <div className="flex-1 min-w-0">
                    <label className="block text-[11px] uppercase tracking-[0.15em] font-sans font-semibold mb-2" style={{ color: '#E91E8C' }}>Search</label>
                    <input
                      data-testid="filter-search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by name, city, profession, or a word from their why..."
                      className="w-full bg-transparent text-sm font-sans text-[#1F2937] placeholder:text-[#4B5563]/30 focus:outline-none pb-2"
                      style={{ borderBottom: `1.5px solid ${search ? '#E91E8C' : '#E5E7EB'}`, transition: 'border-color 0.2s' }}
                    />
                  </div>
                  {/* City */}
                  <div className="w-44">
                    <label className="block text-[11px] uppercase tracking-[0.15em] font-sans font-semibold mb-2" style={{ color: '#E91E8C' }}>City</label>
                    <select
                      data-testid="filter-city"
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                      className="w-full bg-transparent text-sm font-sans text-[#1F2937] focus:outline-none pb-2 appearance-none cursor-pointer"
                      style={{ borderBottom: `1.5px solid ${cityFilter ? '#E91E8C' : '#E5E7EB'}` }}
                    >
                      <option value="">All cities</option>
                      {filters?.cities?.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Profession */}
                  <div className="w-48">
                    <label className="block text-[11px] uppercase tracking-[0.15em] font-sans font-semibold mb-2" style={{ color: '#E91E8C' }}>What they do</label>
                    <select
                      data-testid="filter-role"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="w-full bg-transparent text-sm font-sans text-[#1F2937] focus:outline-none pb-2 appearance-none cursor-pointer"
                      style={{ borderBottom: `1.5px solid ${roleFilter ? '#E91E8C' : '#E5E7EB'}` }}
                    >
                      <option value="">All professions</option>
                      {filters?.roles?.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Theme */}
                  <div className="w-48">
                    <label className="block text-[11px] uppercase tracking-[0.15em] font-sans font-semibold mb-2" style={{ color: '#E91E8C' }}>Their why</label>
                    <select
                      data-testid="filter-theme"
                      value={themeFilter}
                      onChange={(e) => setThemeFilter(e.target.value)}
                      className="w-full bg-transparent text-sm font-sans text-[#1F2937] focus:outline-none pb-2 appearance-none cursor-pointer"
                      style={{ borderBottom: `1.5px solid ${themeFilter ? '#E91E8C' : '#E5E7EB'}` }}
                    >
                      <option value="">All themes</option>
                      {filters?.themes?.map(t => (
                        <option key={t.value} value={t.value}>{t.label} ({t.count})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Filter bar — mobile */}
              <div className="md:hidden mb-6">
                <button
                  data-testid="mobile-filter-toggle"
                  onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-sans font-medium border transition-all"
                  style={{ borderColor: isFiltering ? '#E91E8C' : '#E5E7EB', color: isFiltering ? '#E91E8C' : '#4B5563' }}
                >
                  Filter stories {isFiltering && `(${stories.length})`}
                </button>
                {mobileFiltersOpen && (
                  <div className="mt-4 rounded-2xl p-5 space-y-4" style={{ background: '#FAFAF8' }}>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full bg-transparent text-sm font-sans text-[#1F2937] placeholder:text-[#4B5563]/30 focus:outline-none pb-2"
                      style={{ borderBottom: '1.5px solid #E5E7EB' }}
                    />
                    <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className="w-full bg-transparent text-sm font-sans pb-2" style={{ borderBottom: '1.5px solid #E5E7EB' }}>
                      <option value="">All cities</option>
                      {filters?.cities?.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="w-full bg-transparent text-sm font-sans pb-2" style={{ borderBottom: '1.5px solid #E5E7EB' }}>
                      <option value="">All professions</option>
                      {filters?.roles?.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <select value={themeFilter} onChange={(e) => setThemeFilter(e.target.value)} className="w-full bg-transparent text-sm font-sans pb-2" style={{ borderBottom: '1.5px solid #E5E7EB' }}>
                      <option value="">All themes</option>
                      {filters?.themes?.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <button
                      onClick={() => setMobileFiltersOpen(false)}
                      className="w-full py-2.5 rounded-full text-sm font-sans font-semibold text-white"
                      style={{ background: '#E91E8C' }}
                    >
                      Show {stories.length} stories
                    </button>
                  </div>
                )}
              </div>

              {/* Live count */}
              {isFiltering && (
                <p data-testid="filter-count" className="mb-6" style={{ fontFamily: 'var(--genz-font-hand)', fontSize: '18px', color: '#E91E8C' }}>
                  Showing {stories.length} of {totalApproved} stories
                </p>
              )}
            </>
          )}

          {loading ? (
            <div data-testid="loading-stories" className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="break-inside-avoid rounded-3xl border border-[#E5E7EB] overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-[#FAF9F6]" />
                  <div className="p-8 space-y-3">
                    <div className="h-3 w-24 bg-[#E5E7EB] rounded" />
                    <div className="h-5 w-full bg-[#E5E7EB] rounded" />
                    <div className="h-5 w-3/4 bg-[#E5E7EB] rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : stories.length === 0 && isFiltering ? (
            <div data-testid="no-filter-results" className="text-center py-20">
              <p className="font-sans text-xl text-[#4B5563] mb-4">
                nothing here yet. be the first.
              </p>
              <button
                onClick={() => {
                  const orb = document.querySelector('[data-testid="mira-orb"]');
                  if (orb) orb.click();
                }}
                className="brutal-btn inline-flex items-center gap-2 px-6 py-3 text-sm"
                style={{ background: '#E91E8C', color: '#fff' }}
              >
                talk it out with mira <ArrowRight size={16} />
              </button>
            </div>
          ) : stories.length === 0 ? (
            <div data-testid="no-stories" className="text-center py-20">
              <p className="font-sans text-xl text-[#4B5563]">
                nothing here yet. be the first.
              </p>
              <button
                onClick={() => {
                  const orb = document.querySelector('[data-testid="mira-orb"]');
                  if (orb) orb.click();
                }}
                className="brutal-btn mt-6 inline-flex items-center gap-2 px-6 py-3 text-sm"
                style={{ background: '#E91E8C', color: '#fff' }}
              >
                talk it out with mira <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <>
              <MasonryGrid stories={visibleStories} />

              {/* Quiet searching line */}
              {!isFiltering && (
                <p data-testid="searching-line" className="text-center py-10 hand-note" style={{ color: '#4B5563', fontSize: '20px' }}>
                  some of these stories are finished. some are still becoming. both belong here.
                </p>
              )}

              {/* Silent auto-load sentinel — pauses at autoCap so the bottom stays reachable */}
              {displayCount < stories.length && displayCount < autoCap && (
                <>
                  <div ref={sentinelRef} className="h-4" />
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={24} className="animate-spin text-[#E91E8C]" />
                  </div>
                </>
              )}
              {displayCount < stories.length && displayCount >= autoCap && (
                <div className="flex flex-col items-center py-12 gap-3">
                  <p className="hand-note" style={{ fontSize: '20px', color: '#4B5563' }}>
                    more whys waiting for you
                  </p>
                  <button
                    data-testid="show-more-stories-btn"
                    onClick={() => setAutoCap(prev => prev + PAGE_SIZE * 4)}
                    className="brutal-btn px-8 py-3 text-sm"
                    style={{ background: '#fff', color: '#16130F' }}
                  >
                    keep reading
                  </button>
                </div>
              )}
              {displayCount >= stories.length && stories.length > PAGE_SIZE && !isFiltering && (
                <p className="text-center py-8 hand-note text-lg text-[#4B5563]/60">
                  you've read them all. now share yours.
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* The Quiet Room — doorway to the Icons library */}
      <section data-testid="icons-spotlight" className="py-16 md:py-24" style={{ background: '#FBF7F0' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/because"
            data-testid="quiet-room-doorway"
            className="block px-8 py-10 md:px-10 md:py-12 transition-all duration-500 hover:shadow-lg"
            style={{ background: '#FEFCF7', border: '1.5px solid #C99A2E', borderRadius: '18px' }}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-12">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.3em] font-sans font-semibold mb-5" style={{ color: '#C99A2E' }}>
                  the quiet room &rarr;
                </p>
                <h2 className="mb-3" style={{ fontFamily: 'var(--iab-font-accent)', fontSize: 'clamp(1.7rem, 3.2vw, 2.5rem)', fontWeight: 500, color: '#16130F', lineHeight: 1.2 }}>
                  I am because <em style={{ color: '#C99A2E' }}>they were</em>
                </h2>
                <p className="font-sans text-sm md:text-base" style={{ color: '#8A8378' }}>
                  The Icons Collection &mdash; the whys that built a nation.
                </p>
              </div>
              <div className="flex items-center -space-x-3 flex-shrink-0">
                {[
                  "https://static.wixstatic.com/media/47c50a_c55eb27ee1c74a1faceaab30f8966c5a~mv2.jpg",
                  "https://static.wixstatic.com/media/47c50a_3c24b9119cb64cdea23b05b565040769~mv2.png",
                  "https://static.wixstatic.com/media/47c50a_86c83e74e9b84ecb953421953df42c75~mv2.jpg",
                  "https://static.wixstatic.com/media/47c50a_fc2d727e119940b282617a118b8de49d~mv2.jpg",
                  "https://static.wixstatic.com/media/47c50a_670c6956b6384faabbcc443b58265fec~mv2.jpg",
                ].map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt=""
                    className="iab-bw-portrait w-12 h-12 rounded-full object-cover border-2"
                    style={{ borderColor: '#C99A2E', zIndex: 5 - i }}
                  />
                ))}
              </div>
            </div>
          </Link>
        </div>
      </section>
      <section data-testid="cta-section" className="py-16 md:py-24" style={{ background: '#16130F' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-5xl tracking-tight text-white mb-4" style={{ fontFamily: 'var(--genz-font-display)', fontWeight: 800 }}>
            your turn.
          </h2>
          <p className="font-sans text-lg md:text-xl text-white/70 max-w-lg mx-auto mb-3">
            why do you do what you do? say it out loud.
          </p>
          <p className="hand-note mb-8" style={{ fontSize: '22px', color: 'rgba(251,247,240,0.75)' }}>
            no wrong answers. seriously.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              data-testid="cta-talk-to-mira"
              onClick={() => {
                const orb = document.querySelector('[data-testid="mira-orb"]');
                if (orb) orb.click();
              }}
              className="brutal-btn inline-flex items-center gap-2.5 px-8 py-4 text-base"
              style={{ background: '#E91E8C', color: '#fff', borderColor: '#FBF7F0', boxShadow: '3px 3px 0px rgba(251,247,240,0.9)' }}
            >
              talk it out with mira
              <ArrowRight size={16} />
            </button>
            <Link to="/submit" className="text-sm font-sans font-medium text-white/50 hover:text-white/70 transition-colors">
              or share your why in writing
            </Link>
          </div>
          <p className="mt-4 hand-note" style={{ fontSize: '17px', color: 'rgba(255,255,255,0.4)' }}>
            mira listens. mira asks. mira remembers.
          </p>
        </div>
      </section>
    </div>
  );
}
